import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
  alpha,
} from '@mui/material';
import { useState } from 'react';
import Modal from '../../ui/layout/Modal';
import { submitAiFeedback } from '../../modules/aiConfig';

const FEEDBACK_OPTIONS = [
  {
    value: 'accurate_useful',
    label: 'Accurate & Useful',
    emoji: '✅',
    description: 'Output matched expectations well',
  },
  {
    value: 'needs_minor_improvements',
    label: 'Needs Minor Improvements',
    emoji: '✏️',
    description: 'Mostly good, but could be refined',
  },
  {
    value: 'missing_important_scenarios',
    label: 'Missing Important Details',
    emoji: '⚠️',
    description: 'Important information was missing',
  },
  {
    value: 'not_relevant',
    label: 'Not Relevant',
    emoji: '❌',
    description: 'Output did not match expectations',
  },
];

const AiFeedbackModal = ({
  open,
  onClose,
  entityIds = [],
  entityType,
  agentType,

  projectId,

  title = 'Rate AI Output',
  subtitle = 'Your feedback helps improve AI quality over time.',
}) => {
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setSelected(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const persist = async ({ feedbackResponse, isSkipped }) => {
    try {
      await submitAiFeedback({
        projectId,
        agentType,
        entityType,
        entityIds,
        feedbackResponse,
        isSkipped,
      });
    } catch (err) {
      console.error('AiFeedbackModal: failed to save feedback', err);
    }
  };

  const handleSubmit = async () => {
    if (!selected) return;

    setSubmitting(true);
    await persist({
      feedbackResponse: selected,
      isSkipped: false,
    });
    handleClose();
  };

  const handleSkip = async () => {
    await persist({
      feedbackResponse: null,
      isSkipped: true,
    });
    handleClose();
  };

  const renderActions = () => (
    <>
      <Button
        variant="outlined"
        color="secondary"
        onClick={handleSkip}
        disabled={submitting}
        sx={{ minWidth: 90 }}>
        Skip
      </Button>
      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={!selected || submitting}
        startIcon={
          submitting ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
          )
        }
        sx={{ minWidth: 150 }}>
        {submitting ? 'Submitting…' : 'Submit Feedback'}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      handleClose={undefined}
      modalTitle={title}
      width="sm"
      disableBackdropClose
      action={renderActions}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}>
        {/* Hero */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            mt: 2,
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
          }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'primary.50',
              border: '1.5px solid',
              borderColor: 'primary.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
            <AutoAwesomeIcon
              sx={{
                fontSize: 20,
                color: 'primary.main',
              }}
            />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={600} color="text.primary">
              How was the AI-generated output?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}>
          {FEEDBACK_OPTIONS.map(({ value, label, emoji, description }) => {
            const isActive = selected === value;
            return (
              <Box
                key={value}
                role="button"
                tabIndex={0}
                onClick={() => setSelected(isActive ? null : value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelected(isActive ? null : value);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  borderRadius: 1.5,
                  border: '1.5px solid',
                  borderColor: isActive ? 'primary.main' : 'divider',
                  bgcolor: isActive
                    ? (theme) => alpha(theme.palette.primary.main, 0.06)
                    : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  outline: 'none',
                  '&:hover': {
                    borderColor: 'primary.light',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                  },
                  '&:focus-visible': {
                    boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                }}>
                <Typography
                  sx={{
                    fontSize: 20,
                    lineHeight: 1,
                    flexShrink: 0,
                  }}>
                  {emoji}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}>
                  <Typography
                    variant="body2"
                    fontWeight={isActive ? 600 : 500}
                    color={isActive ? 'primary.main' : 'text.primary'}>
                    {label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {description}
                  </Typography>
                </Box>
                {isActive && (
                  <CheckCircleOutlineIcon
                    sx={{
                      fontSize: 18,
                      color: 'primary.main',
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Modal>
  );
};

export default AiFeedbackModal;